// frontend/src/modules/email/campaign-markdown.ts
//
// Frontend mirror of the backend campaign body renderer
// (api/modules/email/campaign-markdown.ts). The editor preview MUST match what
// recipients actually receive, so the campaign preview uses THIS minimal
// subset — NOT the richer marketing renderMarkdown (which also renders
// headings/lists/images the backend campaign renderer drops).
//
// Like the backend: no raw-HTML passthrough. Every character is escaped first,
// then a small whitelist of markdown constructs is re-introduced on the
// escaped text. Output is safe to feed into dangerouslySetInnerHTML.
//
// Supported subset (must stay in lockstep with the backend):
//   - paragraphs (blank-line separated)
//   - single newlines inside a paragraph → <br />
//   - **bold** and *italic*
//   - [text](url) links — http(s)/mailto only; anything else becomes "#"
//
// Everything else (headings, raw HTML, images, scripts, …) renders as
// escaped literal text. Inline styles only — email clients ignore classes.

const SAFE_URL_RE = /^(https?:\/\/|mailto:)/i;

/** Mirror of api/core/shared/escape-html.ts escapeHtml. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Filter to http(s)/mailto and make the value safe inside a double-quoted attribute. */
function safeUrl(url: string): string {
  const trimmed = url.trim();
  if (!SAFE_URL_RE.test(trimmed)) return '#';
  // escapeHtml already ran over the whole line, so `"` arrives as &quot; —
  // percent-encode any quote forms so the attribute can never be broken out of.
  return trimmed.replace(/&quot;/g, '%22').replace(/"/g, '%22').replace(/'/g, '%27');
}

const P_STYLE =
  'margin:0 0 16px;font-family:Helvetica,Arial,sans-serif;font-size:15px;line-height:1.65;color:#333333;';
const A_STYLE = 'color:#1a73e8;text-decoration:underline;';

/** Inline transforms on an ALREADY-ESCAPED line: links, bold, italic. */
function inline(escaped: string): string {
  let out = escaped;

  // Links — [text](url). Runs first so bold/italic still apply inside link text.
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, text: string, url: string) =>
    `<a href="${safeUrl(url)}" style="${A_STYLE}" target="_blank" rel="noopener">${text}</a>`,
  );

  // Bold — **text** (before italic so ** isn't consumed by *).
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Italic — *text*
  out = out.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  return out;
}

/** Render campaign markdown to email-safe HTML. Empty/blank input → ''. */
export function renderCampaignMarkdown(src: string): string {
  if (!src || !src.trim()) return '';

  const paragraphs = src.replace(/\r\n?/g, '\n').split(/\n{2,}/);
  const out: string[] = [];

  for (const para of paragraphs) {
    const trimmed = para.trim();
    if (!trimmed) continue;
    const body = trimmed
      .split('\n')
      .map((line) => inline(escapeHtml(line)))
      .join('<br />');
    out.push(`<p style="${P_STYLE}">${body}</p>`);
  }

  return out.join('\n');
}
